import { useMemo, useState } from 'react';
import TimeSheetHistoryEntry from 'components/timeSheetHistory/TimeSheetHistoryEntry';
import { isSystemEvent } from 'helper/timeSheetEventLabels';

const SHEET_GROUP = '__sheet__';
const SHEET_GROUP_LABEL = 'Sur le relevé entier';

/** One operation emits one event per sheet. Showing them all would bury the
 *  rest of the timeline under a single admin gesture. */
const collapseCorrelated = (events) => {
  const seen = new Map();
  const result = [];
  events.forEach((event) => {
    if (!event.correlationId) {
      result.push({ event, siblings: 1 });
      return;
    }
    const existing = seen.get(event.correlationId);
    if (existing) {
      existing.siblings += 1;
      return;
    }
    const entry = { event, siblings: 1 };
    seen.set(event.correlationId, entry);
    result.push(entry);
  });
  return result;
};

const groupByDay = (entries) => {
  const groups = [];
  const index = new Map();
  entries.forEach((entry) => {
    const key = entry.event?.dayRef?._id || SHEET_GROUP;
    const label = entry.event?.dayRef?.name || SHEET_GROUP_LABEL;
    if (!index.has(key)) {
      const group = { key, label, entries: [] };
      index.set(key, group);
      groups.push(group);
    }
    index.get(key).entries.push(entry);
  });
  return groups;
};

const TimeSheetHistoryList = ({ events = [], isLoading, error }) => {
  const [showSystem, setShowSystem] = useState(false);

  const groups = useMemo(() => {
    let visible = events;
    if (!showSystem) {
      visible = visible.filter((e) => !isSystemEvent(e));
    }
    return groupByDay(collapseCorrelated(visible));
  }, [events, showSystem]);

  const systemCount = useMemo(() => events.filter(isSystemEvent).length, [events]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 animate-pulse px-4 md:px-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 bg-gray-100 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="ft-sm text-gray-500 px-4 md:px-6">
        L’historique n’a pas pu être chargé. Rouvrez le panneau pour réessayer.
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="ft-sm text-gray-500 px-4 md:px-6">
        Aucun historique détaillé pour ce relevé : les modifications antérieures
        n’ont pas été conservées.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {systemCount > 0 ? (
        <label className="flex items-center gap-2 ft-sm text-gray-500 cursor-pointer px-4 md:px-6 pb-4">
          <input
            type="checkbox"
            checked={showSystem}
            onChange={(e) => setShowSystem(e.target.checked)}
          />
          Afficher les recalculs automatiques ({systemCount})
        </label>
      ) : null}

      {groups.map((group) => (
        <div key={group.key} className="flex flex-col pb-4">
          <div className="sticky top-0 z-10 bg-white px-4 md:px-6 py-2 ft-sm font-bold text-gray-900 border-b border-gray-100">
            {group.label}
          </div>
          <div className="divide-y divide-gray-50 px-4 md:px-6">
            {group.entries.map(({ event, siblings }) => (
              <div key={event._id}>
                <TimeSheetHistoryEntry event={event} />
                {siblings > 1 ? (
                  <div className="ft-sm text-gray-400 pl-16 pb-3">
                    {siblings} relevés touchés par cette opération
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TimeSheetHistoryList;
